import type { EntitySet, EntityType } from '@sap-ux/vocabularies-types';
import type { TemplateType, v4 } from '@sap/ux-specification-types';
import { findPageV4 } from '../utils/utils';
import type { App } from './App';
import type { EntityTypeAnnotations_UI } from '@sap-ux/vocabularies-types/vocabularies/UI_Edm';
import type { PageContext } from '../../common';

/**
 * Represents a single page in a Fiori application.
 * Encapsulates page finding logic and provides convenient access to page properties.
 */
export class Page<Config extends PageContext = PageContext> {
    private readonly app: App;
    readonly target?: v4.SapUiAppPageV4;
    template: {
        name: string;
        type: TemplateType;
    };
    entity?: {
        type?: EntityType;
        set?: EntitySet;
        contextPath?: string;
    };
    config?: Config;

    /**
     * Creates a new Page instance by finding the page configuration in the app.
     *
     * @param app - The App instance containing the pages
     * @param template - The template name to search for
     * @param template.name - The name of the template
     * @param template.type - The type of the template
     * @param entitySet - Optional entity set to match
     * @param entityType - Optional entity type to match
     * @param contextPath - Optional context path to match
     */
    constructor(
        app: App,
        template: { name: string; type: TemplateType },
        entitySet?: EntitySet,
        entityType?: EntityType,
        contextPath?: string
    ) {
        this.app = app;
        this.entity = {
            contextPath: contextPath,
            set: entitySet,
            type: entityType ?? entitySet?.entityType
        };
        this.template = template;
        // Use findPageV4 with app.pages
        this.target = findPageV4(app.pages, template.name, entitySet, contextPath);
    }

    /**
     * Gets the entity type associated with this page.
     *
     * @returns The entity type, or undefined if not found
     */
    getEntityType(): EntityType | undefined {
        return this.entity.type;
    }

    /**
     * Gets the UI annotations for the entity type associated with this page.
     *
     * @returns The UI annotations, or undefined if not found
     */
    getUIAnnotations(): EntityTypeAnnotations_UI | undefined {
        return this.getEntityType()?.annotations?.[this.app.alias];
    }

    /**
     * Gets the alias for the UI vocabulary (default "UI").
     *
     * @returns The alias for the UI vocabulary, or default "UI"
     */
    getUIVocabularyAlias(): string {
        return this.app.getUIVocabularyAlias() || 'UI';
    }

    /**
     * Helper to add configuration properties to the page.
     *
     * @param config Properties to be added to the page
     */
    addConfig(config: Config): void {
        this.config = { ...this.config, ...config };
    }
}
